/**
 * AutomatedSystemsPage — /automated-systems spoke route.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from '@tanstack/react-router';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import Button from '../components/ui/Button.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import {
  automatedSystemsHead,
  automatedSystemsIntro,
  shapes,
  engagementIncludes,
  automatedSystemsCta,
  automatedSystemsLinks,
} from '../content/automatedSystems.js';

const automatedSystemsServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Fully Automated Business Systems',
  description: 'Targeted AI automation for bounded operational pain. Email triage, invoice processing, voice AI for inbound calls, project management overhauls, net-new platform MVPs, and single-integration builds. PRD locked at signing, milestone-based delivery, source code delivered on completion.',
  provider: { '@type': 'Organization', name: "The Artificer's AI" },
  areaServed: { '@type': 'Country', name: 'Australia' },
  serviceType: 'Custom AI automation',
};

const automatedSystemsBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://artificersai.com/' },
    { '@type': 'ListItem', position: 2, name: 'Fully Automated Business Systems', item: 'https://artificersai.com/automated-systems' },
  ],
};

export default function AutomatedSystemsPage() {
  return (
    <>
      <Helmet>
        <title>Fully Automated Business Systems | The Artificer's AI</title>
        <meta name="description" content="Custom AI automation for businesses with a specific operational pain. Email triage, invoice processing, voice AI, and more. No SaaS lock-in." />
        <link rel="canonical" href="https://artificersai.com/automated-systems" />
        <meta property="og:title" content="Fully Automated Business Systems | The Artificer's AI" />
        <meta property="og:description" content="Custom AI automation for businesses with a specific operational pain. Email triage, invoice processing, voice AI, and more. No SaaS lock-in." />
        <meta property="og:url" content="https://artificersai.com/automated-systems" />
      </Helmet>

      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <Reveal as="div" delay={0} className="mb-4"><Eyebrow>{automatedSystemsHead.eyebrow}</Eyebrow></Reveal>
          <Reveal as="h1" delay={80} className="text-display-lg font-display font-semibold mb-4">{automatedSystemsHead.heading}</Reveal>
          <Reveal as="p" delay={140} className="text-body-lg text-ink-200 mb-8">{automatedSystemsHead.subheading}</Reveal>
          <Reveal as="p" delay={200} className="text-body text-ink-200 mb-8">{automatedSystemsIntro}</Reveal>
          <Reveal delay={260}><Button as={Link} to="/intake">{automatedSystemsCta.label}</Button></Reveal>
        </div>
      </section>

      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{shapes.heading}</Reveal>
          <ul className="flex flex-col gap-3">
            {shapes.items.map((item, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="text-body text-ink-200 pl-4 border-l border-white/[.08]">{item}</Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-pad bg-surface text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{engagementIncludes.heading}</Reveal>
          <ul className="flex flex-col gap-3">
            {engagementIncludes.items.map((item, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="text-body text-ink-200 pl-4 border-l border-white/[.08]">{item}</Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <div className="flex flex-col gap-4 mb-10">
            <Reveal delay={0}><Link to={automatedSystemsLinks.foundry.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {automatedSystemsLinks.foundry.label}
            </Link></Reveal>
            <Reveal delay={80}><Link to={automatedSystemsLinks.teamTraining.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {automatedSystemsLinks.teamTraining.label}
            </Link></Reveal>
            <Reveal delay={160}><Link to={automatedSystemsLinks.systems.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {automatedSystemsLinks.systems.label}
            </Link></Reveal>
          </div>
          <Reveal delay={240}><Button as={Link} to="/intake">{automatedSystemsCta.label}</Button></Reveal>
        </div>
      </section>

      <SchemaJsonLd data={automatedSystemsServiceSchema} />
      <SchemaJsonLd data={automatedSystemsBreadcrumb} />
    </>
  );
}
